
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getAdminUsers, 
  addAdminUser, 
  updateAdminUser, 
  removeAdminUser,
  getUserRole,
  AdminUser 
} from '../services/moderationService';

export const useAdminManagement = () => {
  const [userRole, setUserRole] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkUserRole = async () => {
      const role = await getUserRole();
      setUserRole(role);
    };
    checkUserRole();
  }, []);

  const { data: adminUsers, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: getAdminUsers,
    enabled: userRole === 'admin',
  });

  const addAdminMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'admin' | 'moderator' }) =>
      addAdminUser(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const updateAdminMutation = useMutation({
    mutationFn: ({ adminId, role }: { adminId: string; role: 'admin' | 'moderator' }) =>
      updateAdminUser(adminId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const removeAdminMutation = useMutation({
    mutationFn: (adminId: string) => removeAdminUser(adminId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  return {
    adminUsers: adminUsers || [],
    isLoading,
    error,
    userRole,
    isAdmin: userRole === 'admin',
    addAdmin: addAdminMutation.mutate,
    updateAdmin: updateAdminMutation.mutate,
    removeAdmin: removeAdminMutation.mutate,
    isAdding: addAdminMutation.isPending,
    isUpdating: updateAdminMutation.isPending,
    isRemoving: removeAdminMutation.isPending,
  };
};
